import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const OnboardCompanyDialog = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [planTier, setPlanTier] = useState("base");
  const [monthlyRate, setMonthlyRate] = useState("500");
  const [seatCount, setSeatCount] = useState("5");
  const qc = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim() || !adminEmail.trim()) return;

    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Not authenticated");

      const res = await supabase.functions.invoke("provision_company_and_admin", {
        body: { company_name: companyName.trim(), admin_email: adminEmail.trim() },
      });

      if (res.error) throw new Error(res.error.message || "Provisioning failed");
      const companyId = res.data?.company_id;
      if (!companyId) throw new Error("No company_id returned");

      // Create subscription with correct pricing defaults
      const { error: subError } = await supabase
        .from("company_subscriptions" as any)
        .insert({
          company_id: companyId,
          plan_tier: planTier,
          status: "active",
          monthly_rate: parseFloat(monthlyRate) || 500,
          seat_count: parseInt(seatCount) || 5,
          included_seats: 3,
          price_per_additional_seat: 49,
          included_contractors: 10,
          contractor_block_size: 10,
          contractor_block_price: 9,
        } as any);

      if (subError) console.error("Subscription insert error:", subError);

      // Invite the company admin
      await supabase.functions.invoke("invite-user", {
        body: {
          email: adminEmail.trim(),
          company_id: companyId,
          invite_type: "staff",
          role_name: "super_admin",
        },
      });

      toast.success(`${companyName} onboarded successfully. Invite sent to ${adminEmail}.`);
      qc.invalidateQueries({ queryKey: ["platform-summary"] });
      setOpen(false);
      setCompanyName("");
      setAdminEmail("");
      setPlanTier("base");
      setMonthlyRate("500");
      setSeatCount("5");
    } catch (err: any) {
      toast.error(err.message || "Failed to onboard company");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-1" /> Onboard Company
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Onboard New Company</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company-name">Company Name</Label>
            <Input
              id="company-name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Acme Construction"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-email">Admin Email</Label>
            <Input
              id="admin-email"
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@acme.com"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Plan Tier</Label>
              <Select value={planTier} onValueChange={setPlanTier}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="base">Base — $500/mo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="seat-count">Seats</Label>
              <Input
                id="seat-count"
                type="number"
                min={1}
                value={seatCount}
                onChange={(e) => setSeatCount(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="monthly-rate">Base Monthly Rate ($)</Label>
            <Input
              id="monthly-rate"
              type="number"
              min={0}
              step="0.01"
              value={monthlyRate}
              onChange={(e) => setMonthlyRate(e.target.value)}
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Provisioning…" : "Onboard Company"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardCompanyDialog;
